import { useQuery } from "@apollo/client";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { Container } from "@material-ui/core";
import { CloudUpload } from "@material-ui/icons";
import DeleteIcon from "@material-ui/icons/Delete";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import { useTenant, useAdmin } from "src/components/LoggedInAdminProvider";
import PageTitle from "src/components/PageTitle";
import { useSubaccount } from "src/components/SubaccountProvider";
import Table, { TableControls } from "src/components/Table";
import CREATE_UPLOAD from "src/graphql/createUpload";
import {
  bulkDeleteOptOuts,
  bulkDeleteOptOutsVariables
} from "src/graphql/generated/bulkDeleteOptOuts";
import {
  createOptOut,
  createOptOutVariables
} from "src/graphql/generated/createOptOut";
import {
  createUpload,
  createUploadVariables
} from "src/graphql/generated/createUpload";
import {
  deleteOptOut,
  deleteOptOutVariables
} from "src/graphql/generated/deleteOptOut";
import {
  getOptOutJobs,
  getOptOutJobsVariables
} from "src/graphql/generated/getOptOutJobs";
import {
  JobStatus,
  JobType,
  UploadType,
  UploadVisibility
} from "src/graphql/generated/globalTypes";
import {
  listOptOutsVariables,
  listOptOuts,
  listOptOuts_listOptOuts_optOuts
} from "src/graphql/generated/listOptOuts";
import {
  uploadOptOuts,
  uploadOptOutsVariables
} from "src/graphql/generated/uploadOptOuts";
import useMutation from "src/util/useMutation";
import yup from "src/util/yup";

import AddOptOutDialog, { AddOptOutValues } from "./AddOptOutDialog";
import RemoveOptOutsDialog from "./RemoveOptOutsDialog";
import UploadOptOutsDialog from "./UploadOptOutsDialog";
import { OPT_OUT_SORTS } from "./constants";
import {
  BULK_DELETE_OPT_OUTS,
  CREATE_OPT_OUT,
  DELETE_OPT_OUT,
  GET_OPT_OUT_JOBS,
  LIST_OPT_OUTS,
  UPLOAD_OPT_OUTS
} from "./queries";

const OptOuts: React.FC = () => {
  const tenant = useTenant()!;
  const admin = useAdmin()!;

  const { subaccount } = useSubaccount()!;
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, OPT_OUT_SORTS[0].key)
  );
  const sortValue =
    OPT_OUT_SORTS.find(s => s.key === sort)?.value ?? OPT_OUT_SORTS[0].value;

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const {
    loading,
    error,
    data: optOutsData,
    refetch: refetchOptOuts
  } = useQuery<listOptOuts, listOptOutsVariables>(LIST_OPT_OUTS, {
    variables: {
      sort: sortValue,
      filter: {
        phoneNumberContains: search
      },
      offset: page * pageSize,
      limit: pageSize,
      subaccountId: subaccount.id
    }
  });

  const [addOptOutDialogOpen, toggleAddOptOutDialog] = useState(false);
  const [addOptOut] = useMutation<createOptOut, createOptOutVariables>(
    CREATE_OPT_OUT,
    {
      onErrorSnackbarMessage: "Error adding opt out",
      refetchQueries: ["listOptOuts"],
      awaitRefetchQueries: true
    }
  );
  async function handleAddOptOut(values: AddOptOutValues) {
    const { errors } = await addOptOut({
      variables: { ...values, subaccountId: subaccount.id }
    });
    if (errors) {
      throw errors;
    } else {
      toggleAddOptOutDialog(false);
    }
  }

  const {
    data: uploadsData,
    previousData: previousUploadsData,
    startPolling: startUploadPolling,
    stopPolling: stopUploadPolling
  } = useQuery<getOptOutJobs, getOptOutJobsVariables>(GET_OPT_OUT_JOBS, {
    variables: {
      jobType: JobType.OPT_OUT_UPLOAD,
      statuses: [JobStatus.CREATED, JobStatus.QUEUED, JobStatus.RUNNING],
      limit: 1,
      subaccountId: subaccount.id
    }
  });
  const {
    data: bulkDeleteData,
    previousData: previousBulkDeletesData,
    startPolling: startBulkDeletePolling,
    stopPolling: stopBulkDeletePolling
  } = useQuery<getOptOutJobs, getOptOutJobsVariables>(GET_OPT_OUT_JOBS, {
    variables: {
      jobType: JobType.OPT_OUT_BULK_DELETE,
      statuses: [JobStatus.CREATED, JobStatus.QUEUED, JobStatus.RUNNING],
      limit: 1,
      subaccountId: subaccount.id
    }
  });
  const isUploading = !!uploadsData?.listJobs.totalCount;
  const isBulkDeleting = !!bulkDeleteData?.listJobs.totalCount;
  const wasUploading = !!previousUploadsData?.listJobs.totalCount;
  const wasBulkDeleting = !!previousBulkDeletesData?.listJobs.totalCount;

  const usePollForJob = (
    processing: boolean,
    startPolling: (interval: number) => void,
    stopPolling: () => void
  ) => {
    const updatePollingInterval = useCallback(
      (interval: number) => {
        stopPolling();
        startPolling(interval);
      },
      [startPolling, stopPolling]
    );
    useEffect(() => {
      if (isUploading) {
        updatePollingInterval(500);
      } else {
        updatePollingInterval(5000);
      }
      return () => stopPolling();
    }, [processing, startPolling, stopPolling, updatePollingInterval]);
  };

  usePollForJob(isUploading, startUploadPolling, stopUploadPolling);
  usePollForJob(isBulkDeleting, startBulkDeletePolling, stopBulkDeletePolling);

  useEffect(() => {
    if (
      (wasUploading && !isUploading) ||
      (wasBulkDeleting && !isBulkDeleting)
    ) {
      // if upload just completed, refetch opt outs
      void refetchOptOuts();
    }
  }, [
    wasUploading,
    isUploading,
    wasBulkDeleting,
    isBulkDeleting,
    refetchOptOuts
  ]);

  const [uploadDialogOpen, toggleUploadDialog] = useState(false);
  const [removeDialogOpen, toggleRemoveDialog] = useState(false);
  const [getPrivateUploadURL] = useMutation<
    createUpload,
    createUploadVariables
  >(CREATE_UPLOAD, {
    onErrorSnackbarMessage: "Failed to access upload storage"
  });
  const [startOptOutUpload] = useMutation<
    uploadOptOuts,
    uploadOptOutsVariables
  >(UPLOAD_OPT_OUTS, {
    onErrorSnackbarMessage: "Error uploading opt outs",
    refetchQueries: ["getOptOutJobs"],
    awaitRefetchQueries: true
  });
  async function handleUploadOptOuts({
    file,
    phoneNumberColumn
  }: {
    file: File;
    phoneNumberColumn: string;
  }) {
    const getUploadUrlResponse = await getPrivateUploadURL({
      variables: {
        type: UploadType.OPT_OUTS,
        contentType: "application/csv",
        fileName: file.name,
        visibility: UploadVisibility.PRIVATE
      }
    });
    if (getUploadUrlResponse.errors) {
      throw getUploadUrlResponse.errors;
    }

    // Upload the file
    await axios.put(
      getUploadUrlResponse.data!.createUpload.presignedPutURL,
      file,
      { headers: { "Content-Type": "application/csv" } }
    );

    const { errors } = await startOptOutUpload({
      variables: {
        phoneNumberColumn,
        uploadToken: getUploadUrlResponse.data!.createUpload.token,
        subaccountId: subaccount.id
      }
    });
    if (errors) {
      throw errors;
    } else {
      toggleUploadDialog(false);

      if (!isUploading) {
        // the job completed before the job query completed
        await refetchOptOuts();
      }
    }
  }

  const [deleteOptOut] = useMutation<deleteOptOut, deleteOptOutVariables>(
    DELETE_OPT_OUT,
    {
      onErrorSnackbarMessage: "Error opting number back in",
      refetchQueries: ["listOptOuts"]
    }
  );
  async function handleDeleteOptOut(optOut: listOptOuts_listOptOuts_optOuts) {
    await deleteOptOut({ variables: { id: optOut.id } });
  }

  const [startOptOutBulkDelete] = useMutation<
    bulkDeleteOptOuts,
    bulkDeleteOptOutsVariables
  >(BULK_DELETE_OPT_OUTS, {
    onErrorSnackbarMessage: "Error bulk removing opt outs",
    refetchQueries: ["getOptOutJobs"],
    awaitRefetchQueries: true
  });
  async function handleRemoveOptOuts() {
    const { errors } = await startOptOutBulkDelete({
      variables: {
        subaccountId: subaccount.id
      }
    });
    if (errors) {
      throw errors;
    } else {
      toggleRemoveDialog(false);
      if (!isBulkDeleting) {
        // the job completed before the job query completed
        await refetchOptOuts();
      }
    }
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  const anyBulkOptOutJobIsRunning = isUploading || isBulkDeleting;
  const bulkDeleteDisabled = !admin.isTenantOwner || anyBulkOptOutJobIsRunning;
  let bulkDeleteTooltip;
  if (!admin.isTenantOwner) {
    bulkDeleteTooltip =
      "Opt-out clearing is only available to organization owners";
  } else if (anyBulkOptOutJobIsRunning) {
    bulkDeleteTooltip =
      "Cannot clear the list while other opt-out processing is in-progress.";
  }

  return (
    <Container>
      <PageTitle title="Do Not Call List" />
      <div>{`Add phone numbers to this list to remove them from all future communication from any call campaigns in this dialer ${
        tenant.isUsingSubaccounts ? "subaccount" : "organization"
      }. Delete phone numbers from this list to make them immediately eligible to be dialed by call campaigns in this dialer ${
        tenant.isUsingSubaccounts ? "subaccount" : "organization"
      }.`}</div>
      <TableControls
        addButtons={[
          {
            label: "Add Phone Number",
            onClick: () => toggleAddOptOutDialog(true)
          },
          {
            label: isUploading ? "Uploading..." : "Upload a CSV",
            icon: <CloudUpload />,
            onClick: () => toggleUploadDialog(true),
            disabled: anyBulkOptOutJobIsRunning,
            tooltipTitle: anyBulkOptOutJobIsRunning
              ? "Cannot upload while other opt-out processing is in-progress."
              : undefined
          },
          {
            label: isBulkDeleting ? "Clearing..." : "Clear list",
            icon: null,
            onClick: () => toggleRemoveDialog(true),
            disabled: bulkDeleteDisabled,
            tooltipTitle: bulkDeleteTooltip
          }
        ]}
        filters={[]}
        sort={{
          value: sort,
          onChange: setSort,
          options: OPT_OUT_SORTS.map(option => ({
            value: option.key,
            label: option.label
          }))
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search for a phone number...",
          schema: yup
            .string()
            .test(
              "phoneNumberSubstring",
              "Please include at least 3 digits",
              value =>
                !value ||
                value.length === 0 ||
                value.replace(/\D/g, "").length >= 3
            )
        }}
      />
      {loading ? (
        <LoadingIndicator />
      ) : (
        <Table
          columns={[
            {
              key: "phoneNumber",
              title: "Phone Number",
              renderValue: optOut => optOut.phoneNumber
            },
            {
              key: "createdBy",
              title: "Added By",
              renderValue: optOut =>
                optOut.createdByAdmin?.fullName ??
                optOut.createdByJob?.createdByAdmin?.fullName
            },
            {
              key: "createdAt",
              title: "Added At",
              renderValue: optOut => new Date(optOut.createdAt).toLocaleString()
            }
          ]}
          rows={optOutsData?.listOptOuts.optOuts ?? []}
          rowActions={[
            {
              tooltipLabel: "Re-enable calling for this phone number",
              onClick: handleDeleteOptOut,
              confirm: optOut => ({
                title: "Re-enable Calling",
                description: (
                  <p>
                    Are you sure you would like to opt{" "}
                    <strong>{optOut.phoneNumber}</strong> back in for future{" "}
                    communications?
                  </p>
                )
              }),
              icon: <DeleteIcon />
            }
          ]}
          totalCount={optOutsData?.listOptOuts.totalCount ?? 0}
          rowsPerPage={pageSize}
          onChangeRowsPerPage={setPageSize}
          page={page}
          onChangePage={setPage}
          emptyState="No do-not-calls found"
        />
      )}
      <AddOptOutDialog
        open={addOptOutDialogOpen}
        onClose={() => toggleAddOptOutDialog(false)}
        onSubmit={handleAddOptOut}
      />
      <UploadOptOutsDialog
        open={uploadDialogOpen}
        onClose={() => toggleUploadDialog(false)}
        onSubmit={handleUploadOptOuts}
      />
      <RemoveOptOutsDialog
        open={removeDialogOpen}
        onClose={() => toggleRemoveDialog(false)}
        onSubmit={handleRemoveOptOuts}
      />
    </Container>
  );
};

export default OptOuts;
